import React from 'react'
import { Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material'
import * as datefns from 'date-fns'
import { EnrichedIssueEntity } from '../../../types/IssueEntity'
import { calculateSumSubscriptionCommitmentInPercentage } from '../utils'
import { formatDateYYYYMMDD, formatNumber, formatPercentage } from '../../../packages/utils/numberFormatters'
import { useTranslation } from 'react-i18next'
import { useAppLocale } from '../../../i18n/localeUtils'

const calcTotalRightsCounts = ({
    totalOfferedUnits,
    unitsReceived,
    rightsRequired,
}: {
    [key: string]: number | null
}) => {
    if (totalOfferedUnits === null || unitsReceived === null || rightsRequired === null) return null

    return (totalOfferedUnits / unitsReceived) * rightsRequired
}

const formatVwapPercentage = ({ hasShares, locale, vwap, rightsRequired, unitsReceived, unitSubPrice, t }) => {
    const vwapPercentage =
        vwap === null || rightsRequired === null || unitsReceived === null || unitSubPrice === null
            ? null
            : (vwap * rightsRequired) / unitsReceived / unitSubPrice

    if (vwapPercentage === null) return '-'
    if (parseFloat(unitSubPrice) === 0) return '-'

    return (
        formatPercentage(vwapPercentage * 100, { decimals: 1, locale }) +
        ' ' +
        t(hasShares ? 'pages.issues.tradingStats.ofPricePerShare' : 'pages.issues.tradingStats.ofPricePerUnit')
    )
}

const formatQuantityPercentage = ({ locale, quantity, sumTotalRightsExclCommitment, t }) => {
    const quantityPercentage =
        quantity !== null && sumTotalRightsExclCommitment !== null
            ? (quantity / sumTotalRightsExclCommitment) * 100
            : null

    if (quantityPercentage === null) return '-'

    return (
        formatPercentage(quantityPercentage, {
            decimals: 0,
            locale,
        }) +
        ' ' +
        t('pages.issues.tradingStats.ofAvailableRights')
    )
}

type TradingStatsRightsProps = Pick<
    EnrichedIssueEntity,
    | 'approxCommitment'
    | 'unitSubPrice'
    | 'totalOfferedUnits'
    | 'unitsReceived'
    | 'rightsRequired'
    | 'rightsEndDate'
    | 'rightsStartDate'
    | 'investors'
> & {
    endDateQuantity: null | number
    endDateVwap: null | number
    hasShares: boolean
    tradeQuantity: null | number
    vwap: null | number
    totalOfferingSize: number | null
}

function TradingStatsRights({
    approxCommitment,
    endDateQuantity,
    endDateVwap,
    hasShares,
    totalOfferedUnits,
    unitsReceived,
    rightsRequired,
    rightsEndDate,
    rightsStartDate,
    tradeQuantity,
    investors,
    unitSubPrice,
    vwap,
    totalOfferingSize,
}: TradingStatsRightsProps) {
    const { t } = useTranslation()
    const { language: locale } = useAppLocale()

    if (!rightsStartDate) {
        return t('pages.issues.tradingStats.rightsNotStarted')
    }

    if (new Date() < new Date(rightsStartDate)) {
        return t('pages.issues.tradingStats.rightsStartDate', {
            date: formatDateYYYYMMDD(rightsStartDate, { locale }),
        })
    }

    const filteredInvestors = investors.filter((p) => !p.isSeller)
    const sumTotalRights = calcTotalRightsCounts({ totalOfferedUnits, unitsReceived, rightsRequired })
    const hasTaRow = filteredInvestors.some((p) => p.feeLevel === 0)
    const sumSubscriptionCommitmentInPercentage = hasTaRow
        ? calculateSumSubscriptionCommitmentInPercentage({
              investors: filteredInvestors.filter((i) => !i.withoutRights),
              totalOfferingSize,
          })
        : approxCommitment
    const isOngoing =
        rightsEndDate === null ? true : datefns.startOfDay(new Date(rightsEndDate)) >= datefns.startOfDay(new Date())

    const sumTotalRightsExclCommitment =
        sumTotalRights !== null && sumSubscriptionCommitmentInPercentage !== null
            ? ((100 - Number(sumSubscriptionCommitmentInPercentage)) / 100) * sumTotalRights
            : sumTotalRights

    return (
        <TableContainer>
            <Table>
                <TableBody>
                    <TableRow>
                        <TableCell>{t('pages.issues.tradingStats.totalRights')}</TableCell>
                        <TableCell>
                            {sumTotalRights === null ? '-' : formatNumber(sumTotalRights, { locale, decimals: 0 })}
                        </TableCell>
                        <TableCell />
                    </TableRow>
                    <TableRow>
                        <TableCell>{t('pages.issues.tradingStats.approxRightsExclTa')}</TableCell>
                        <TableCell>
                            {sumTotalRightsExclCommitment === null
                                ? '-'
                                : formatNumber(sumTotalRightsExclCommitment, {
                                      locale,
                                      decimals: 0,
                                  })}
                        </TableCell>
                        <TableCell />
                    </TableRow>

                    <TableRow>
                        <TableCell>
                            {isOngoing
                                ? t('pages.issues.tradingStats.tradedRightsOngoing')
                                : t('pages.issues.tradingStats.tradedRightsTotal')}
                        </TableCell>
                        <TableCell>
                            {tradeQuantity === null ? '-' : formatNumber(tradeQuantity, { locale, decimals: 0 })}
                        </TableCell>
                        <TableCell>
                            {formatQuantityPercentage({
                                quantity: tradeQuantity,
                                sumTotalRightsExclCommitment,
                                locale,
                                t,
                            })}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell>{t('pages.issues.tradingStats.tradedRightsLastDay')}</TableCell>
                        {isOngoing && (
                            <TableCell colSpan={2}>
                                {t('pages.issues.tradingStats.lastDay', {
                                    date: formatDateYYYYMMDD(rightsEndDate, { locale }),
                                })}
                            </TableCell>
                        )}
                        {!isOngoing && (
                            <>
                                <TableCell>
                                    {endDateQuantity === null
                                        ? '-'
                                        : formatNumber(endDateQuantity, { locale, decimals: 0 })}
                                </TableCell>
                                <TableCell>
                                    {formatQuantityPercentage({
                                        quantity: endDateQuantity,
                                        sumTotalRightsExclCommitment,
                                        locale,
                                        t,
                                    })}
                                </TableCell>
                            </>
                        )}
                    </TableRow>

                    <TableRow>
                        <TableCell>{t('pages.issues.tradingStats.rightsVwapTotal')}</TableCell>
                        <TableCell>
                            {vwap === null ? '-' : formatNumber(vwap, { minDecimals: 2, maxDecimals: 5, locale })}
                        </TableCell>
                        <TableCell>
                            {formatVwapPercentage({
                                hasShares,
                                unitsReceived,
                                unitSubPrice,
                                locale,
                                rightsRequired,
                                vwap,
                                t,
                            })}
                        </TableCell>
                    </TableRow>

                    <TableRow>
                        <TableCell>{t('pages.issues.tradingStats.rightsVwapLastDay')}</TableCell>
                        {isOngoing && (
                            <TableCell colSpan={2}>
                                {t('pages.issues.tradingStats.lastDay', {
                                    date: formatDateYYYYMMDD(rightsEndDate, { locale }),
                                })}
                            </TableCell>
                        )}
                        {!isOngoing && (
                            <>
                                <TableCell>
                                    {endDateVwap === null
                                        ? '-'
                                        : formatNumber(endDateVwap, { minDecimals: 2, maxDecimals: 5, locale })}
                                </TableCell>
                                <TableCell>
                                    {formatVwapPercentage({
                                        hasShares,
                                        unitsReceived,
                                        unitSubPrice,
                                        locale,
                                        rightsRequired,
                                        vwap: endDateVwap,
                                        t,
                                    })}
                                </TableCell>
                            </>
                        )}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default TradingStatsRights
